import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

interface LoginPageProps {
  mode?: 'vendor' | 'tenant';
}

interface DemoData {
  vendors: { id: string; name: string }[];
  tenants: { id: string; name: string }[];
  organizations: { id: string; name: string }[];
  users: {
    id: string;
    name: string;
    email: string;
    role: string;
    vendorId: string | null;
    tenantId: string | null;
    organizationId: string | null;
  }[];
  error?: string;
}

export default function LoginPage({ mode: initialMode }: LoginPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading: authLoading } = useAuth();

  // Detect mode from props, path, or hostname
  const getDetectedMode = (): 'vendor' | 'tenant' => {
    if (initialMode) return initialMode;
    if (location.pathname.startsWith('/platform')) return 'vendor';
    const hostname = globalThis.location.hostname;
    if (hostname.includes('platform') || hostname.includes('v-')) return 'vendor';
    return 'tenant';
  };
  const detectedMode = getDetectedMode();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Demo Data State
  const [demoData, setDemoData] = useState<DemoData | null>(null);
  const [selectedOrgType, setSelectedOrgType] = useState<'vendor' | 'tenant'>(detectedMode);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [showResetPassword, setShowResetPassword] = useState(false);

  // Sync org type when mode changes
  useEffect(() => {
    setSelectedOrgType(detectedMode);
  }, [detectedMode]);

  useEffect(() => {
    if (import.meta.env.DEV || import.meta.env.VITE_DEMO_MODE === 'true') {
      // Use relative path to leverage Vite proxy in dev, or VITE_API_BASE in prod
      const baseUrl = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_BASE ?? '');
      fetch(`${baseUrl}/api/dev/demo-data`)
        .then((res) => res.json())
        .then((rawData) => {
          const data = rawData as DemoData;
          if (!data || data.error || !data.vendors || !data.tenants) {
            console.warn('Invalid demo data received', data);
            return;
          }
          setDemoData(data);
          // Default selection logic based on mode
          if (detectedMode === 'vendor' && data.vendors.length > 0) {
            setSelectedOrgId(data.vendors[0].id);
          } else if (data.tenants.length > 0) {
            // Default to seeded Akita corporation if found
            const akita = data.tenants.find((t) => t.id === '1010001000012');
            setSelectedOrgId(akita ? akita.id : data.tenants[0].id);
          }
        })
        .catch((err) => console.error('Failed to fetch demo data:', err));
    }
  }, [detectedMode]);

  // Filter users based on selection
  const filteredUsers =
    demoData?.users.filter((u) => {
      if (selectedOrgType === 'vendor') {
        return u.vendorId === selectedOrgId;
      } else {
        return u.tenantId === selectedOrgId;
      }
    }) || [];

  // Get return URL from query params or default to a safe start page
  const from =
    (location.state as { from?: string })?.from ||
    (detectedMode === 'vendor' ? '/system/tenants' : '/master/organizations');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const result = await login(email, password);
    setIsSubmitting(false);

    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error || t('auth.loginFailed'));
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-blue-900">{t('common.systemName', 'University Admission System')}</h1>
          <p className="text-gray-600 mt-2">
            {detectedMode === 'vendor'
              ? t('common.platformSubtitle', 'Platform Provider Portal')
              : t('common.systemSubtitle', 'Management Portal')}
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {detectedMode === 'vendor' ? t('auth.platformLogin', 'Platform Admin Login') : t('auth.adminLogin')}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="email@example.com"
                required
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="••••••••"
                required
                autoComplete="current-password"
                minLength={8}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}

            {/* Submit Button */}
            <Button type="submit" className="w-full py-2.5" disabled={isSubmitting}>
              {isSubmitting ? t('auth.loggingIn') : t('auth.login')}
            </Button>
          </form>

          {/* Demo Accounts (Dev/Demo Mode) */}
          {/* Password Reset Modal (Mock) */}
          {showResetPassword && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full">
                <h3 className="text-lg font-bold mb-4">{t('auth.resetPassword.title')}</h3>
                <p className="text-sm text-gray-600 mb-4">{t('auth.resetPassword.description')}</p>
                <input type="email" placeholder="email@example.com" className="w-full border p-2 rounded mb-4" />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowResetPassword(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={() => {
                      alert(t('auth.resetPassword.sent'));
                      setShowResetPassword(false);
                    }}
                  >
                    {t('auth.resetPassword.send')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Forgot Password Link */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setShowResetPassword(true)}
              className="text-sm text-blue-600 hover:underline"
            >
              {t('auth.forgotPassword')}
            </button>
          </div>

          {/* Dynamic Demo Login */}
          {(import.meta.env.DEV || import.meta.env.VITE_DEMO_MODE === 'true') && demoData && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-4 font-bold uppercase tracking-wider">{t('auth.demoMode')}</p>

              <div className="space-y-4">
                {/* Organization Selection */}
                {/* Organization Selection */}
                <div>
                  <label htmlFor="org-select" className="block text-xs font-medium text-gray-500 mb-1">
                    {detectedMode === 'vendor'
                      ? t('auth.selectVendor', 'Select Provider')
                      : t('auth.selectTenant', 'Select Tenant')}
                  </label>
                  <select
                    id="org-select"
                    className="w-full border border-gray-300 rounded p-2 text-sm"
                    value={selectedOrgId}
                    onChange={(e) => setSelectedOrgId(e.target.value)}
                  >
                    {detectedMode === 'vendor'
                      ? demoData.vendors.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))
                      : demoData.tenants.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                  </select>
                </div>

                {/* User Selection */}
                <div>
                  <label htmlFor="user-select" className="block text-xs font-medium text-gray-500 mb-1">
                    {t('auth.selectUser')}
                  </label>
                  <select
                    id="user-select"
                    className="w-full border border-gray-300 rounded p-2 text-sm"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                  >
                    <option value="">{t('auth.selectUserPlaceholder')}</option>
                    {filteredUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Login Button */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
                  disabled={!selectedUserId}
                  onClick={async () => {
                    if (!selectedUserId) return;
                    try {
                      const baseUrl = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_BASE ?? '');
                      const res = await fetch(`${baseUrl}/api/dev/login-as`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ userId: selectedUserId }),
                      });

                      if (res.ok) {
                        const { data } = (await res.json()) as { data: { user: unknown; accessToken: string } };
                        // Set local storage matching AuthContext expectation
                        localStorage.setItem(
                          'admission-system-auth',
                          JSON.stringify({
                            user: data.user,
                            accessToken: data.accessToken,
                          }),
                        );
                        // Force reload to pick up auth state
                        globalThis.location.href = from;
                      } else {
                        const err = (await res.json()) as { error?: string };
                        setError(err.error || t('auth.loginFailed'));
                      }
                    } catch (e) {
                      console.error(e);
                      setError('Network Connection Error');
                    }
                  }}
                >
                  {t('auth.loginAsSelected')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 flex flex-col items-center gap-4">
          <LanguageSwitcher />

          <div className="text-center text-xs text-gray-500">
            <p className="font-semibold text-red-500 mb-1">
              {t('auth.demoWarning', 'DEMO ENVIRONMENT - Unauthorized reproduction prohibited')}
            </p>
            &copy; {new Date().getFullYear()} {t('common.copyright', 'All rights reserved.')}
          </div>
        </div>
      </div>
    </div>
  );
}
